import React from 'react';
import {
  getLinkedOrganizationsPending,
  getLinkedOrganizationsSuccess,
  getLinkedOrganizationsError,
  setBannerState,
  claimLinkedOrganizationAdminPending,
  claimLinkedOrganizationAdminSuccess,
  claimLinkedOrganizationAdminError,
  unlinkOrganizationPending,
  unlinkOrganizationSuccess,
  unlinkOrganizationError,
  toggleProcessedItem,
  toggleModal,
  toggleForm,
  triggerModal,
  revalidateOrgLinkWithPinPending,
  revalidateOrgLinkWithPinSuccess,
  revalidateOrgLinkWithPinError,
  getOrganizationMemberRolesPending,
  getOrganizationMemberRolesSuccess,
  getOrganizationMemberRolesError,
  removeRoleFromUserConfirm,
  getCustomerPermissionsPending,
  getCustomerPermissionsSuccess,
  getUserRolesPending,
  getUserRolesSuccess,
  getUserRolesError,
} from '../actions/AppActions';
import { createLogic } from 'redux-logic';
import { formatAccounts, sortAlphabetically } from '../utils/profileHelpers';
import { formatGroups, formatOrgMemberRoles } from '../utils/orgHelpers';
import intl from '../actions/lang/en.json';
import { ENABLE_REFACTORED_PAGINATED_ORGS, PATH_PROFILE, PERMISSIONS, UNLINK_ORG_CONFIRM } from '../constants';
import history from '../utils/history';

export const DEFAULT_PAGE_SIZE = 100;

const getPaginatedOrgs = async (
  action,
  page = 0,
  pageSize = DEFAULT_PAGE_SIZE
) => {
  const { data: orgs } = await action({ page, pageSize });
  if (orgs.data && page < orgs.totalPages - 1) {
    const nextPageData = await getPaginatedOrgs(action, page + 1, pageSize);
    return [...orgs.data, ...nextPageData];
  }

  return orgs.data;
};

const getLegacyPaginatedOrgs = async(
  action,
  page = 0,
  pageSize = DEFAULT_PAGE_SIZE) => {
  const { data: orgs, headers } = await action({ page, pageSize });
  const totalOrgs = headers?.totalElements;

  if (
    orgs != null &&
    ((totalOrgs && totalOrgs > (page + 1) * pageSize) ||
      (!totalOrgs && orgs.length === pageSize))
  ) {
    const rest = await getLegacyPaginatedOrgs(action, page + 1, pageSize);
    return [...orgs, ...rest];
  } else {
    return orgs;
  }
};

export const getOrganizationsLogic = createLogic({
  type: getLinkedOrganizationsPending().type,
  latest: true,
  process({ httpClient, action }, dispatch, done) {
    const { userId } = action?.payload;
    const apiCall = params => httpClient(dispatch).get('/organizations', { params });
    const fetchOrgs = ENABLE_REFACTORED_PAGINATED_ORGS ? getPaginatedOrgs : getLegacyPaginatedOrgs;
    return fetchOrgs(apiCall)
      .then(orgs => {
        dispatch(getLinkedOrganizationsSuccess(sortAlphabetically(formatAccounts(formatGroups(orgs, userId)))));
        dispatch(getCustomerPermissionsPending({orgs, customerId: userId}));
        done();
      })
      .catch(error => {
        dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.Organizations.Get_Linked_Orgs_Error } }));
        dispatch(getLinkedOrganizationsError(error));
        done();
      });
  }
});

export const getOrganizationMemberRolesLogic = createLogic({
  type: getOrganizationMemberRolesPending().type,
  latest: true,
  process({ httpClient, getState, action }, dispatch, done) {
    const {userIds, organizationId} = action?.payload;
	const state = getState();
    return httpClient(dispatch).post('/customer/roles', {customerIds: userIds})
    .then(resp => {
      const formattedMemberRoles = formatOrgMemberRoles(state, resp.data, organizationId);
      dispatch(getOrganizationMemberRolesSuccess(formattedMemberRoles));
      done();
    })
	.catch(error => {
      dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.Organizations.Get_Linked_Org_Member_Roles_Error } }));
      dispatch(getOrganizationMemberRolesError(error));
      done();
    });
  }
});

export const getUserRolesLogic = createLogic({
  type: getUserRolesPending().type,
  process({ httpClient, getState, action }, dispatch, done) {
    const { customerId } = action?.payload;
    return httpClient(dispatch).get(`/organization/customers/${customerId}/roles`)
    .then(resp => {
      dispatch(getUserRolesSuccess(resp.data));
      done();
    })
	.catch(error => {
      dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.Organizations.Get_User_Roles_Error } }));
      dispatch(getUserRolesError(error));
      done();
    });
  }
});

const createPermissionObject = (permissionId, organizationId, account) => {
  const permissionObject = {
    permissionId,
    permissionScope: {
        organization: organizationId,
    }
  };

  if(account) {
    permissionObject.permissionScope['account'] = account;
  }

  return permissionObject;
};

const getCustomerPermissionsPayload = (orgs, customerId) => {
  const permissionArray = orgs.flatMap(org =>
    PERMISSIONS.map(permission => {
      const permissionsWithAccountNumbers = ['PayWithACH', 'ViewTransactionHistory', 'ManageACHPaymentMethods'];

      if (permissionsWithAccountNumbers.includes(permission.displayId) && org?.organizationProfile.billingAccounts) {
        return org?.organizationProfile.billingAccounts.map(account =>
          createPermissionObject(permission.permissionId, org.organizationId, account.accountNumber)).flat();
      }

      return createPermissionObject(permission.permissionId, org.organizationId);
    })
  );

  return {
    customerId,
    queryPermissions: permissionArray.flat(),
  };
};

const getPermissions = async(action, page = 0, pageSize = DEFAULT_PAGE_SIZE) => {
  const { data: permissions } = await action({ page, pageSize });
  if (permissions.content && page < permissions.totalPages - 1) {
    const nextPageData = await getPermissions(action, page + 1, pageSize);
    return [...permissions.content, ...nextPageData];
  }

  return permissions.content;
};

export const getCustomerPermissionsLogic = createLogic({
  type: getCustomerPermissionsPending().type,
  latest: true,
  process({ httpClient, getState, action }, dispatch, done) {
    const { customerId, orgs } = action.payload;
    const customerPermissions = getCustomerPermissionsPayload(orgs, customerId);
    const permissionCall = params => httpClient(dispatch).post(`/organization/customer/permissions?page=${params?.page}&size=${params?.pageSize}`, {
      ...customerPermissions,
  });

    return getPermissions(permissionCall)
    .then(resp => {
      dispatch(getCustomerPermissionsSuccess(resp));
      done();
    })
    .catch(error => {
      dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.Organizations.Get_Customer_Permissions_Error } }));
      dispatch(getOrganizationMemberRolesError(error));
      done();
    });
  }
});

export const unlinkOrganizationConfirmationLogic = createLogic({
  type: UNLINK_ORG_CONFIRM,
  latest: true,
  processOptions: {
    dispatchMultiple: true,
  },
  warnTimeout: 0,
  process({ action }, dispatch) {
    const { organizationName, userId, organizationId, userCanUnlink } = action.payload;
    const unlinkMessage = `You are about to unlink ${organizationName}. You will no longer be able to make purchases on behalf of this organization or view this profile. Do you want to unlink?`;
    const addAdminMessage = 'You must add another administrator before you can unlink this organization.';
    const modalProps = userCanUnlink
      ? {
        show: true,
        message: unlinkMessage,
        okLabel: 'Yes',
        okDataHook: 'unlinkOrg.yes',
        cancelLabel: 'No',
        cancelDataHook: 'unlinkOrg.no',
        buttonOrder: 'rtl',
        okClick: () => {
          dispatch(toggleProcessedItem(organizationId));
          dispatch(unlinkOrganizationPending({ userId, organizationId }));
          dispatch(toggleModal());
        },
        cancelClick: () => {
          history.push(PATH_PROFILE);
          dispatch(toggleModal());
        }
      }
      : {
        show: true,
        message: addAdminMessage,
        okLabel: 'Go to Groups',
        okDataHook: 'unlinkOrg.goToGroups',
        cancelLabel: 'Go Back',
        cancelDataHook: 'unlinkOrg.goBack',
        buttonOrder: 'rtl',
        okClick: () => {
          history.push(`/organization/${organizationId}/groups`);
          dispatch(toggleModal());
        },
        cancelClick: () => {
          history.push(PATH_PROFILE);
          dispatch(toggleModal());
        }
      };
    dispatch(toggleForm(null));
    dispatch(triggerModal(modalProps));
  }
});

export const unlinkOrganizationLogic = createLogic({
  type: unlinkOrganizationPending().type,
  latest: true,
  process({ httpClient, action, getState}, dispatch, done) {
    const { callbackFn, payload } = action;
    const { userId, organizationId } = payload;
    const unlinkCustomerCommand = {
      command: {
        RemoveAssociatedCustomer: {
          aggregateID: organizationId,
          associatedCustomerId: userId,
          requestingContext: {
            requestingProcess: 'UNLINK_FROM_ORGANIZATION',
          },
        },
      },
    };
    return httpClient(dispatch)
      .post('/unlink-customer', unlinkCustomerCommand)
      .then(() => {
        dispatch(unlinkOrganizationSuccess(organizationId));
        if (callbackFn) callbackFn();
      })
      .catch(error => {
        dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.Organizations.Unlink_Organization_Error } }));
        dispatch(unlinkOrganizationError(error));
      })
      .finally(() => done());
  }
});

export const claimOrganizationAdminLogic = createLogic({
  type: claimLinkedOrganizationAdminPending().type,
  latest: true,

  process({ httpClient, action }, dispatch, done) {
    const { orgs, organizationId, userIds, userId, errorCallbackFn, bannerLocation } = action?.payload;

    return httpClient(dispatch).post(`/organizations/${organizationId}/admin`, {})
    .then(({ data }) => {
      const { adminGroupId } = data?.data;
      dispatch(claimLinkedOrganizationAdminSuccess({ organizationId, adminGroupId }));
      setTimeout(() => {
        dispatch(getCustomerPermissionsPending({orgs, customerId: userId}))
        dispatch(getOrganizationMemberRolesPending({userIds: userIds, organizationId}))
        done();
      }, 2000);
    })
    .catch(error => {
      dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.Organizations.Claim_Linked_Org_Admin_Error, location: (bannerLocation || '') } }));
      dispatch(claimLinkedOrganizationAdminError(error));
      if (errorCallbackFn) errorCallbackFn();
      done();
    });
  }
});

export function buildAddRoleCommand(customerId, roleId, organizationId, accountNumber, requestingUser){
    const roleCommand = {
        'command': {
          AddRoleToCustomer: {
            aggregateID: customerId,
            roleId: roleId,
            scopeVariables: {
              'organization': organizationId,
              'group': '*'
            },
            requestingContext: {
              requestingDomain: 'MLC',
              requestingUserId: requestingUser,
              requestingOrganizationId: organizationId
            },
          }
        }
      };

    if (accountNumber) {
      roleCommand.command.AddRoleToCustomer.scopeVariables['account'] = accountNumber;
    } else {
      roleCommand.command.AddRoleToCustomer.scopeVariables['account'] = '*';
    }

    return roleCommand;
}

export const removeRoleConfirmLogic = createLogic({
  type: removeRoleFromUserConfirm().type,
  latest: true,
  processOptions: {
    dispatchMultiple: true,
  },
  warnTimeout: 0,
  process({ action }, dispatch) {
    const {
      organizationId,
      groupId,
      roleInfo,
      roleId,
      userId,
      requestingUser,
      groupName,
      personId,
      name,
      email,
      bannerLocation,
      userCannotRemoveMember,
      openAddMembersForm,
      onConfirm,
      successCallbackFn,
    } = action?.payload;

    const MessageCmpt = () => (
      <span>
        {userCannotRemoveMember ? (
          <>You must add another adminstrator before you can remove {name}.</>
        ) : (
          <>
            You are about to remove the {roleInfo?.name} role from {name} (
            {email}).
            <br />
            Are you sure?
          </>
        )}
      </span>
    );

    const modalProps = userCannotRemoveMember
      ? {
          show: true,
          message: <MessageCmpt />,
          okLabel: 'Add Administrator',
          okDataHook: 'removeUserGroup.goBack',
          buttonOrder: 'rtl',
          okClick: () => {
            dispatch(toggleModal());
            dispatch(openAddMembersForm());
          },
        }
      : {
          show: true,
          message: <MessageCmpt />,
          okLabel: 'Yes',
          okDataHook: 'removeUserGroup.yes',
          cancelLabel: 'No',
          cancelDataHook: 'removeUserGroup.no',
          buttonOrder: 'rtl',
          okClick: () => {
            onConfirm({
              organizationId,
              roleId,
              roleInfo,
              userId,
              requestingUser,
              groupName,
              groupId,
              personId,
              email,
              bannerLocation,
              successCallbackFn,
            }, {
              onError: () => {
                dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.Organizations.Remove_Role_From_User_Error.replace(':email', email), location: (bannerLocation || '') } }));
              }
            });
            dispatch(toggleModal());
          },
          cancelClick: () => {
            dispatch(toggleModal());
          },
        };
    dispatch(triggerModal(modalProps));
  },
});

export const relinkBillingAccountLogic = createLogic({
  type: revalidateOrgLinkWithPinPending().type,
  latest: true,
  process({ httpClient, action: { payload } }, dispatch, done) {
    const { accountNumber, pin, id, errorCallback } = payload;
    httpClient(dispatch).put('/linked-billing-accounts',
      { accountNumber, pin, billingAccountLinkId: id }
    ).then(res => {
      dispatch(revalidateOrgLinkWithPinSuccess(res.data));
    }).catch(error => {
      dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.Organizations.Revalidate_Pin_Error } }));
      dispatch(revalidateOrgLinkWithPinError(error));
      errorCallback && errorCallback(intl.Errors.Organizations.Revalidate_Pin_Error);
    }).finally(() => done());
  }
});

export default [
  getOrganizationsLogic,
  claimOrganizationAdminLogic,
  unlinkOrganizationConfirmationLogic,
  unlinkOrganizationLogic,
  relinkBillingAccountLogic,
  getOrganizationMemberRolesLogic,
  removeRoleConfirmLogic,
  getCustomerPermissionsLogic,
  getUserRolesLogic,
];
